import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Box,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Select,
  MenuItem,
  Menu,
  Avatar,
  InputBase,
} from '@mui/material';
import { styled, createTheme, ThemeProvider } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import EmailIcon from '@mui/icons-material/Email';
import SendIcon from '@mui/icons-material/Send';
import DashboardIcon from '@mui/icons-material/Dashboard';
import EditIcon from '@mui/icons-material/Edit';
import PersonIcon from '@mui/icons-material/Person';
import AddIcon from '@mui/icons-material/Add';
import SettingsIcon from '@mui/icons-material/Settings';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import logo from '../logo.png';
import ScriptManager from './ScriptManager';
import AutomationSettings from './AutomationSettings';
import BarterDealAutomation from './BarterDealAutomation';
import GmailAccountManager from './GmailAccountManager';
import TemplateEditor from './TemplateEditor';
import { useAuth } from '../context/AuthContext';
import DashboardPage from './DashboardPage';
import { Routes, Route, useLocation } from 'react-router-dom';
import InfluencersTable from './InfluencersTable';

const drawerWidth = 80;

const theme = createTheme({
  palette: {
    background: {
      default: '#ffffff',
    },
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          boxShadow: '0px 0px 5px rgba(0, 0, 0, 0.1)',
          border: '1px solid #e0e0e0',
        },
      },
    },
  },
});

const Root = styled('div')(({ theme }) => ({
  display: 'flex',
  backgroundColor: theme.palette.background.default,
  overflowX: 'hidden',
  width: '100%'
}));

const AppBarStyled = styled(AppBar)(({ theme }) => ({
  zIndex: theme.zIndex.drawer + 1,
  backgroundColor: theme.palette.background.default,
  color: theme.palette.text.primary,
  boxShadow: 'none',
  borderBottom: '1px solid #e0e0e0',
}));

const DrawerStyled = styled(Drawer)(({ theme }) => ({
  width: drawerWidth,

  flexShrink: 0,
  '& .MuiDrawer-paper': {
    width: drawerWidth,
    boxSizing: 'border-box',
    border: 'none',
    backgroundColor: theme.palette.background.default,
  },
}));

const MainContent = styled('main')(({ theme }) => ({
  flexGrow: 1,
  padding: theme.spacing(3),
  overflowX: 'hidden',
  width: `calc(100% - ${drawerWidth}px)`
}));

const StyledIconButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.primary.main,
}));

const Dashboard = () => {
  const { logout } = useAuth();
  const [selectedIcon, setSelectedIcon] = useState(() => {
    return parseInt(localStorage.getItem('selectedIcon') || '0');
  });
  const [anchorEl, setAnchorEl] = useState(null);
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);

  useEffect(() => {
    localStorage.setItem('selectedIcon', selectedIcon.toString());
  }, [selectedIcon]);

  const handleSettingsClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleClose();
    logout();
  };

  return (
    <ThemeProvider theme={theme}>
      <Root>
        <AppBarStyled position="fixed">
          <Toolbar>
            <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>
              <img src={logo} alt="Maniko Tech Logo" style={{ height: '20px', marginRight: '10px' }} />
              {isSearchExpanded && (
                <InputBase
                  placeholder="Search..."
                  sx={{
                    ml: 1,
                    flex: 1,
                    '& .MuiInputBase-input': {
                      width: '100%',
                      transition: theme.transitions.create('width'),
                      [theme.breakpoints.up('sm')]: {
                        width: '12ch',
                        '&:focus': {
                          width: '20ch',
                        },
                      },
                    },
                  }}
                />
              )}
            </Box>

            <IconButton color="inherit">
              <PersonIcon />
            </IconButton>
            <Avatar
              alt="User"
              src="/path-to-user-image.jpg"
              sx={{ width: 32, height: 32, marginRight: 2, marginLeft: 1 }}
            />

            <IconButton
              color="inherit"
              onClick={handleSettingsClick}
              aria-controls="settings-menu"
              aria-haspopup="true"
            >
              <SettingsIcon />
            </IconButton>
            <Menu
              id="settings-menu"
              anchorEl={anchorEl}
              keepMounted
              open={Boolean(anchorEl)}
              onClose={handleClose}
            >
              <MenuItem onClick={handleLogout}>Logout</MenuItem>
            </Menu>
          </Toolbar>
        </AppBarStyled>
        <DrawerStyled variant="permanent">
          <Toolbar />
          <List>
            {[DashboardIcon, EmailIcon, SendIcon, PersonIcon, AccountCircleIcon].map((Icon, index) => (
              <ListItem 
                button 
                key={index} 
                sx={{ justifyContent: 'center' }}
                onClick={() => setSelectedIcon(index)}
              >
                <StyledIconButton 
                  sx={{ 
                    backgroundColor: selectedIcon === index ? '#F8F2EC' : 'transparent',
                    '&:hover': {
                      backgroundColor: selectedIcon === index ? '#E5DFD9' : '#F8F2EC',
                    }
                  }}
                >
                  <Icon />
                </StyledIconButton>
              </ListItem>
            ))}
          </List>
        </DrawerStyled>
        <MainContent>
          <Toolbar />
          <Routes>
            <Route path="/influencers" element={<InfluencersTable />} />
            <Route
              path="*"
              element={
                <>
                  {selectedIcon === 0 && <DashboardPage />}
                  {selectedIcon === 1 && <AutomationSettings />}
                  {selectedIcon === 2 && <TemplateEditor />}
                  {selectedIcon === 3 && <BarterDealAutomation />}
                  {selectedIcon === 4 && <GmailAccountManager />}
                </>
              }
            />
          </Routes>
        </MainContent>
      </Root>
    </ThemeProvider>
  );
};

export default Dashboard;